import { useEffect, useState } from 'react'
import api from '../api-client'
import { toast } from 'react-toastify'
import { FaEdit, FaTrash } from "react-icons/fa"
import { scrollToTop } from '../lib'
import { useAppState } from '../hooks'
import TenantProductForm from './tenant-product-form'
import { dateAndTime, dollars } from '@oneethos/shared'

const TenantProductList = () => {
  const { tenant: { config: tenant } } = useAppState()
  const [list, setList] = useState([])
  const [isEditing, setIsEditing] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [isDisplayingList, setIsDisplayingList] = useState(true)
  const [productID, setProductID] = useState<string>('')
  const tenantID = tenant?._id

  useEffect(() => {
    fetchProducts()
  }, [])

  const fetchProducts = () => {
    api.get(`/tenant/${tenantID}/products`)
      .then(response => {
        setList([...response.map(p => ({
          ...p,
          effectiveDate: p.effectiveDate ? new Date(p.effectiveDate) : null, 
          expirationDate: p.expirationDate ? new Date(p.expirationDate) : null
        }))])
      })
      .catch(error => {
        toast.error(`Error fetching products: ${error.message}`)
      })
  }

  const handleCreateProduct = (newProduct) => {
    setList(prevList => [...prevList, newProduct])
    onList()
  }

  const handleEditProduct = (updatedProduct) => {
    setList(prevList =>
      prevList.map(product => product.id === updatedProduct.id ? updatedProduct : product))
    onList()
  }

  const onEdit = (productId: string) => {
    setIsEditing(!isEditing)
    setIsDisplayingList(!isDisplayingList)
    setProductID(productId)
  }

  const onAdd = () => {
    setIsAdding(!isAdding)
    setIsDisplayingList(!isDisplayingList)
  }

  const onList = () => {
    setIsDisplayingList(!isDisplayingList)
    setIsEditing(false)
    setIsAdding(false)
    scrollToTop()
  }

  const formatDate = (date) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <>
      {isAdding && <TenantProductForm onSave={handleCreateProduct} tenantID={tenantID} />}
      {isEditing &&
        <TenantProductForm productID={productID} onEdit={handleEditProduct} tenantID={tenantID} />
      }
      {list.length > 0 && isDisplayingList &&
        <>
          <div className="table-list-wrap">
            <table className="table">
              <tbody>
                <tr className='text-center'>
                  <th className='text-start'>Product Details</th>
                  <th>Term</th>
                  <th className='text-center'>
                    <div>APR / Rate</div>
                    <div className='text-muted small fw-normal'>autopay</div>
                  </th>
                  <th className='text-center'>
                    <div>APR / Rate</div>
                    <div className='text-muted small fw-normal'>no autopay</div>
                  </th>
                  <th className='text-start'>Loan Amounts</th>
                  <th className='text-start'>Dates</th>
                  <th></th>
                </tr>
                {list.map(product =>
                  <tr key={product.id} className='text-center'>
                    <td className='text-start'>
                      <div>{product.decisionProProductName || '(missing name)'}</div>
                      <div className="text-muted small">
                        <span className="badge bg-primary me-2">{product.productType}</span>
                        {product.productDescription && (
                          <div className="mt-1">{product.productDescription}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="fw-bold">{product.term}</div>
                      <div className="small text-muted">months</div>
                    </td>
                    <td className='text-center text-small'>
                      <div>{product.autoPayAPR}% / {product.interestRateWithAutopay}%</div>
                    </td>
                    <td className='text-center text-small'>
                      <div>{product.apr}% / {product.interestRateWithoutAutopay}%</div>
                    </td>
                    <td className='text-start text-small'>
                      <div><strong>Min:</strong> {dollars(product.minLoanAmount)}</div>
                      <div><strong>Max:</strong> {dollars(product.maxLoanAmount)}</div>
                    </td>
                    <td className='text-start text-small'>
                      <div><strong>Effective:</strong> {formatDate(product.effectiveDate)}</div>
                      <div><strong>Expires:</strong> {formatDate(product.expirationDate)}</div>
                    </td>
                    <td>
                      <FaEdit style={{ cursor: 'pointer' }} color='gray' onClick={() => onEdit(product.id)} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>}
      {isDisplayingList && <button type="button" className="btn btn-secondary btn-sm" onClick={onAdd}>Add a product</button>}
      {!isDisplayingList &&
        <div className="buttons mt-3">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onList}>List of products</button>
        </div>
      }
    </>
  )
}

export default TenantProductList
